import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, ArrowRight, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdersPage = () => {
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user) {
      fetchOrders();
    }
  }, [user, authLoading]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-20 flex items-center justify-center">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20">
      <section className="section-padding">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">My Orders</h1>
          </motion.div>

          {orders.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Package className="w-20 h-20 text-[#E5D3B3]/30 mx-auto mb-6" />
              <h2 className="font-serif text-3xl text-[#F5F2EB] mb-4">No Orders Yet</h2>
              <p className="text-[#A3988F] mb-8">Start shopping to see your orders here</p>
              <Link to="/fashion" className="btn-primary inline-flex items-center space-x-2" data-testid="orders-shop-btn">
                <span>Shop Now</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-6">
              {orders.map((order, index) => (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="luxury-card rounded-xl p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <p className="text-xs tracking-[0.2em] uppercase text-[#A3988F] mb-1">Order ID</p>
                      <p className="text-[#F5F2EB] font-mono">{order.id}</p>
                    </div>
                    <div className="mt-4 md:mt-0 md:text-right">
                      <p className="text-xs tracking-[0.2em] uppercase text-[#A3988F] mb-1">Order Date</p>
                      <p className="text-[#F5F2EB]">{new Date(order.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}</p>
                    </div>
                  </div>

                  <div className="border-t border-b border-[#E5D3B3]/10 py-4 my-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-[#A3988F] text-sm">{order.items?.length || 0} item(s)</p>
                      </div>
                      <div className="mt-2 md:mt-0 flex items-center space-x-4">
                        <span className={`text-sm px-3 py-1 rounded-full ${
                          order.status === 'processing' ? 'bg-[#FAD6C6]/20 text-[#FAD6C6]' :
                          order.status === 'completed' ? 'bg-[#E5D3B3]/20 text-[#E5D3B3]' :
                          order.status === 'shipped' ? 'bg-[#8C7B6B]/20 text-[#8C7B6B]' :
                          'bg-[#A3988F]/20 text-[#A3988F]'
                        }`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                        <span className="text-xl font-semibold text-[#E5D3B3]">${order.total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <button className="flex items-center space-x-2 text-[#E5D3B3] hover:text-[#FAD6C6] transition-colors text-sm">
                    <span>View Details</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default OrdersPage;
